import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import PassagePicker from '../components/PassagePicker';
import { getStoredGeminiModel } from '../utils/geminiModel';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import './Dashboard.css';

const emptyMessage = { type: '', text: '' };

const questionFields = [
  { key: 'question', label: 'question', rows: 3 },
  { key: 'correct', label: 'correct', rows: 2 },
  { key: 'correctExplain', label: 'correctExplain', rows: 3 },
  { key: 'wrong1', label: 'wrong1', rows: 2 },
  { key: 'wrongExplain1', label: 'wrongExplain1', rows: 3 },
  { key: 'wrong2', label: 'wrong2', rows: 2 },
  { key: 'wrongExplain2', label: 'wrongExplain2', rows: 3 },
  { key: 'wrong3', label: 'wrong3', rows: 2 },
  { key: 'wrongExplain3', label: 'wrongExplain3', rows: 3 },
];

function normalizeQuestion(raw) {
  return {
    question: raw?.question == null ? '' : String(raw.question),
    correct: raw?.correct == null ? '' : String(raw.correct),
    correctExplain: raw?.correctExplain == null ? '' : String(raw.correctExplain),
    wrong1: raw?.wrong1 == null ? '' : String(raw.wrong1),
    wrongExplain1: raw?.wrongExplain1 == null ? '' : String(raw.wrongExplain1),
    wrong2: raw?.wrong2 == null ? '' : String(raw.wrong2),
    wrongExplain2: raw?.wrongExplain2 == null ? '' : String(raw.wrongExplain2),
    wrong3: raw?.wrong3 == null ? '' : String(raw.wrong3),
    wrongExplain3: raw?.wrongExplain3 == null ? '' : String(raw.wrongExplain3),
  };
}

function PassageQuestion() {
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  const [questionCount, setQuestionCount] = useState(5);
  const [level, setLevel] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState(emptyMessage);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingError, setExistingError] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [deletingId, setDeletingId] = useState(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const res = await api.get('/data/passage-multiple-choice/summary');
      setSummaryRows(res.data.rows || []);
    } catch (err) {
      setSummaryRows([]);
      setSummaryError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to load summary'
      );
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadExistingQuestions = useCallback(async (pid) => {
    if (!pid) {
      setExistingQuestions([]);
      setExistingError('');
      setExistingLoading(false);
      setExpandedIds(new Set());
      return;
    }

    setExistingLoading(true);
    setExistingError('');
    setExpandedIds(new Set());
    try {
      const res = await api.get('/data/multiple-choice', { params: { passageId: pid } });
      setExistingQuestions(res.data.questions || []);
    } catch (err) {
      setExistingQuestions([]);
      setExistingError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to load existing questions'
      );
    } finally {
      setExistingLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) setPassages(res.data.passages || []);
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };

    load();
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [loadSummary]);

  useEffect(() => {
    const pid = parseInt(passageId, 10);
    if (!passageId || Number.isNaN(pid)) {
      loadExistingQuestions(null);
      return;
    }
    loadExistingQuestions(pid);
  }, [passageId, loadExistingQuestions]);

  const selectedPassageTitle = useMemo(() => {
    const pid = parseInt(passageId, 10);
    return passages.find((p) => Number(p.passage_id) === pid)?.title || '';
  }, [passageId, passages]);

  const handlePassageChange = useCallback((nextId) => {
    setPassageId(nextId);
    setQuestions([]);
    setMessage(emptyMessage);
  }, []);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteExisting = useCallback(
    async (mcId) => {
      if (!window.confirm('Delete this question?')) return;

      setDeletingId(mcId);
      setMessage(emptyMessage);
      try {
        await api.delete(`/data/multiple-choice/${mcId}`);
        setExistingQuestions((prev) => prev.filter((q) => q.multipleChoiceId !== mcId));
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.delete(mcId);
          return next;
        });
        setMessage({ type: 'success', text: 'Question deleted.' });
        await loadSummary();
      } catch (err) {
        setMessage({
          type: 'error',
          text:
            err.response?.data?.details ||
            err.response?.data?.error ||
            'Failed to delete question',
        });
      } finally {
        setDeletingId(null);
      }
    },
    [loadSummary]
  );

  const updateQuestion = useCallback((index, field, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const deleteQuestion = useCallback((index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validateBaseInputs = () => {
    if (!passageId) {
      setMessage({ type: 'error', text: 'Select a passage.' });
      return null;
    }

    const pid = parseInt(passageId, 10);
    if (Number.isNaN(pid)) {
      setMessage({ type: 'error', text: 'Passage must be valid.' });
      return null;
    }

    const countInt = parseInt(questionCount, 10);
    if (Number.isNaN(countInt) || countInt < 1 || countInt > 40) {
      setMessage({ type: 'error', text: 'Number of Questions must be an integer between 1 and 40.' });
      return null;
    }

    const levelInt = parseInt(level, 10);
    if (Number.isNaN(levelInt)) {
      setMessage({ type: 'error', text: 'Level must be a valid integer.' });
      return null;
    }

    return { pid, countInt, levelInt };
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    const inputs = validateBaseInputs();
    if (!inputs) return;

    setGenerateLoading(true);
    setMessage(emptyMessage);
    try {
      const res = await api.post('/data/passage-multiple-choice/generate', {
        passageId: inputs.pid,
        questionCount: inputs.countInt,
        model: getStoredGeminiModel(),
      });
      const nextQuestions = (res.data.questions || []).map(normalizeQuestion);
      setQuestions(nextQuestions);
      setMessage({
        type: 'success',
        text: `Generated ${nextQuestions.length} question(s). Review before saving.`,
      });
    } catch (err) {
      setQuestions([]);
      setMessage({
        type: 'error',
        text:
          err.response?.data?.details ||
          err.response?.data?.error ||
          'Failed to generate passage questions',
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const inputs = validateBaseInputs();
    if (!inputs) return;

    if (questions.length === 0) {
      setMessage({ type: 'error', text: 'No generated questions to save.' });
      return;
    }

    for (let i = 0; i < questions.length; i += 1) {
      const missing = questionFields.some(({ key }) => !questions[i][key]?.trim());
      if (missing) {
        setMessage({ type: 'error', text: `Question ${i + 1} is missing required fields.` });
        return;
      }
    }

    setSaveLoading(true);
    setMessage(emptyMessage);
    try {
      const res = await api.post('/data/insert-multiple-choice', {
        passageId: inputs.pid,
        level: inputs.levelInt,
        questions,
      });
      setQuestions([]);
      setMessage({
        type: 'success',
        text: res.data.message || `Saved ${res.data.inserted_count} question(s).`,
      });
      await Promise.all([loadSummary(), loadExistingQuestions(inputs.pid)]);
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.details ||
          err.response?.data?.error ||
          'Failed to save passage questions',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const busy = generateLoading || saveLoading || deletingId != null;

  return (
    <div className="dashboard">
      <DashboardHeader title="Passage question" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 1100, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Passage Question</h2>
            <p className="form-hint" style={{ marginBottom: 20 }}>
              Generate multiple-choice comprehension questions from the selected passage, review them,
              then save to multiple_choice.
            </p>

            <form onSubmit={handleGenerate} className="ai-generation-form">
              <div className="form-group">
                <PassagePicker
                  inputId="passage-question-picker"
                  label={
                    <>
                      Passage <span className="required">*</span>
                    </>
                  }
                  passages={passages}
                  passagesLoading={passagesLoading}
                  passagesError={passagesError}
                  value={passageId}
                  onChange={handlePassageChange}
                  extraDisabled={busy}
                />
                {selectedPassageTitle ? (
                  <div className="selected-passage">Selected: {selectedPassageTitle}</div>
                ) : null}
              </div>

              {passageId ? (
                <div className="existing-questions-section">
                  <h3 className="subsection-title">
                    Existing questions
                    {!existingLoading && !existingError
                      ? ` (${existingQuestions.length})`
                      : ''}
                  </h3>
                  {existingLoading ? (
                    <p className="overview-meta">Loading questions…</p>
                  ) : existingError ? (
                    <div className="message error">{existingError}</div>
                  ) : existingQuestions.length === 0 ? (
                    <p className="overview-meta">No saved questions for this passage.</p>
                  ) : (
                    <div className="existing-questions-list">
                      {existingQuestions.map((q) => {
                        const id = q.multipleChoiceId;
                        const open = expandedIds.has(id);
                        return (
                          <div key={id} className="existing-question-item">
                            <div className="existing-question-header">
                              <button
                                type="button"
                                className="existing-question-toggle"
                                onClick={() => toggleExpanded(id)}
                                aria-expanded={open}
                              >
                                <span className="existing-question-arrow">
                                  {open ? '▼' : '▶'}
                                </span>
                                <span className="existing-question-stem">
                                  {q.question || '(empty question)'}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="delete-question-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExisting(id);
                                }}
                                disabled={busy}
                              >
                                {deletingId === id ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                            {open ? (
                              <div className="existing-question-options">
                                <div>
                                  <strong>Correct:</strong> {q.correct}
                                </div>
                                <div>
                                  <strong>Wrong 1:</strong> {q.wrong1}
                                </div>
                                <div>
                                  <strong>Wrong 2:</strong> {q.wrong2}
                                </div>
                                <div>
                                  <strong>Wrong 3:</strong> {q.wrong3}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label htmlFor="passage-question-count">
                    Number of Questions <span className="required">*</span>
                  </label>
                  <input
                    id="passage-question-count"
                    type="number"
                    min="1"
                    max="40"
                    step="1"
                    value={questionCount}
                    onChange={(e) => setQuestionCount(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="passage-question-level">
                    Level <span className="required">*</span>
                  </label>
                  <input
                    id="passage-question-level"
                    type="number"
                    step="1"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="submit-button" disabled={busy || !passageId}>
                {generateLoading ? 'Generating...' : 'Generate'}
              </button>
            </form>

            {questions.length > 0 ? (
              <form onSubmit={handleSave} style={{ marginTop: 28 }}>
                <h3 className="subsection-title">Generated Questions</h3>
                <div className="generated-questions-list">
                  {questions.map((question, index) => (
                    <div key={index} className="mc-question-group">
                      <div className="question-item-header">
                        <h4 className="question-item-title">Question {index + 1}</h4>
                        <button
                          type="button"
                          className="delete-question-button"
                          onClick={() => deleteQuestion(index)}
                          disabled={busy}
                        >
                          Delete
                        </button>
                      </div>

                      {questionFields.map(({ key, label, rows }) => (
                        <div className="form-group" key={key}>
                          <label htmlFor={`passage-question-${index}-${key}`}>
                            {label} <span className="required">*</span>
                          </label>
                          <textarea
                            id={`passage-question-${index}-${key}`}
                            value={question[key]}
                            onChange={(e) => updateQuestion(index, key, e.target.value)}
                            rows={rows}
                            disabled={busy}
                            required
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <button type="submit" className="submit-button" disabled={busy || questions.length === 0}>
                  {saveLoading ? 'Saving...' : 'Save'}
                </button>
              </form>
            ) : null}
          </div>

          <div className="form-section">
            <h3 className="subsection-title">Passage question counts</h3>
            <p className="form-hint" style={{ marginBottom: 16 }}>
              Saved multiple-choice questions per passage (sorted by passage_id).
            </p>
            {summaryLoading ? (
              <p className="overview-meta">Loading summary…</p>
            ) : summaryError ? (
              <div className="message error">{summaryError}</div>
            ) : summaryRows.length === 0 ? (
              <p className="overview-meta">No passages in the database.</p>
            ) : (
              <div className="overview-table-wrap">
                <table className="overview-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th className="overview-col-numeric">Number of questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((row) => (
                      <tr key={row.passageId}>
                        <td>{row.title}</td>
                        <td className="overview-col-numeric">{row.questionCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PassageQuestion;
